<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { canAccessRoute } from '@/routerAccess';
import { useAccessContextStore } from '@/stores/accessContextStore';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';
import { useUserStore } from '@/stores/user';

const props = defineProps<{
    isSidebarOpen: boolean;
}>();

const router = useRouter();
const route = useRoute();

const user = useUserStore();

const auth = useAuthStore();
const accessContextStore = useAccessContextStore();
const tenantStore = useTenantStore();

const getTenants = computed(() => {
    return user.tenants;
});

const selectedTenant = ref<string>();

// This watchEffect is needed to properly setup the current tenant after a login
// because watch(user) does not work (this probably means that we do not use descope correctly )
watchEffect(async () => {
    if (selectedTenant.value !== undefined) {
        return;
    }

    if (!auth.isAuthenticated) {
        return;
    }
    if (user.tenants !== undefined) {
        const tenants = user.tenants;
        if (tenants && tenants.length > 0) {
            selectedTenant.value = tenants[0].tenantName;
            tenantStore.updateTenant(tenants[0]);
        }
    }
});

watch(selectedTenant, async (tenantName) => {
    const tenants = user.tenants;
    if (!tenants) {
        return;
    }

    const tenant = tenants.find(
        (tenant: any) => tenant.tenantName === tenantName
    );
    if (tenant) {
        tenantStore.updateTenant(tenant);

        await accessContextStore.ensureLoaded(true);

        if (
            !canAccessRoute(route, {
                isAuthenticated: auth.isAuthenticated,
                tenantPermissions: {
                    tenantAdmin: tenantStore.tenantAdmin,
                    tenantElqano: tenantStore.tenantElqano
                },
                accessEntitlements: {
                    loaded: accessContextStore.loaded,
                    hasActiveSubscription:
                        accessContextStore.hasActiveSubscription,
                    hasCompletedOnboarding:
                        accessContextStore.hasCompletedOnboarding
                }
            })
        ) {
            router.push('/');
        }
    }
});
</script>

<template>
    <Select
        :id="'tenant-selector'"
        v-if="getTenants && getTenants.length > 1 && props.isSidebarOpen"
        v-model="selectedTenant"
    >
        <SelectTrigger class="bg-transparent border-border">
            <SelectValue>
                {{ selectedTenant }}
            </SelectValue>
        </SelectTrigger>
        <SelectContent>
            <SelectItem
                v-for="tenant in getTenants"
                :key="tenant.tenantId"
                :value="tenant.tenantName"
            >
                {{ tenant.tenantName }}
            </SelectItem>
        </SelectContent>
    </Select>
</template>
